/**
 * Minimal DXF R12 exporter for 2D drawing interchange.
 * Generates DXF entities from SVG view paths and annotations.
 * Compatible with AutoCAD, LibreCAD, Onshape DXF import.
 */
import type { Annotation, Vec2 } from "./annotationTypes";
import { dist2d } from "./annotationTypes";
import { SHEET_W, SHEET_H } from "./constants";
import { downloadBlob } from "./downloadBlob";

// DXF uses Y-up, SVG uses Y-down. Flip Y.
function flipY(y: number): number {
  return SHEET_H - y;
}

function dxfHeader(): string {
  return `0
SECTION
2
HEADER
9
$ACADVER
1
AC1009
9
$INSBASE
10
0.0
20
0.0
30
0.0
9
$EXTMIN
10
0.0
20
0.0
30
0.0
9
$EXTMAX
10
${SHEET_W}
20
${SHEET_H}
30
0.0
0
ENDSEC
`;
}

function dxfTables(): string {
  return `0
SECTION
2
TABLES
0
TABLE
2
LTYPE
70
3
0
LTYPE
2
CONTINUOUS
70
0
3
Solid line
72
65
73
0
40
0.0
0
LTYPE
2
HIDDEN
70
0
3
Hidden line
72
65
73
2
40
6.0
49
4.0
49
-2.0
0
LTYPE
2
CENTER
70
0
3
Center line
72
65
73
4
40
12.0
49
8.0
49
-2.0
49
2.0
49
-2.0
0
ENDTAB
0
TABLE
2
LAYER
70
4
0
LAYER
2
0
70
0
62
7
6
CONTINUOUS
0
LAYER
2
VISIBLE
70
0
62
7
6
CONTINUOUS
0
LAYER
2
HIDDEN
70
0
62
8
6
HIDDEN
0
LAYER
2
DIMENSIONS
70
0
62
1
6
CONTINUOUS
0
ENDTAB
0
ENDSEC
`;
}

function dxfLine(x1: number, y1: number, x2: number, y2: number, layer: string): string {
  return `0
LINE
8
${layer}
10
${x1.toFixed(4)}
20
${flipY(y1).toFixed(4)}
30
0.0
11
${x2.toFixed(4)}
21
${flipY(y2).toFixed(4)}
31
0.0
`;
}

function dxfText(x: number, y: number, height: number, text: string, layer: string): string {
  return `0
TEXT
8
${layer}
10
${x.toFixed(4)}
20
${flipY(y).toFixed(4)}
30
0.0
40
${height.toFixed(4)}
1
${text}
`;
}

function dxfCircle(cx: number, cy: number, r: number, layer: string): string {
  return `0
CIRCLE
8
${layer}
10
${cx.toFixed(4)}
20
${flipY(cy).toFixed(4)}
30
0.0
40
${r.toFixed(4)}
`;
}

/** Parse SVG path "M x y L x y ..." into line segments */
function svgPathToLines(pathData: string, offsetX: number, offsetY: number): Array<[number, number, number, number]> {
  const lines: Array<[number, number, number, number]> = [];
  const commands = pathData.match(/[MLZmlz][^MLZmlz]*/g);
  if (!commands) return lines;

  let cx = 0, cy = 0;
  let startX = 0, startY = 0;

  for (const cmd of commands) {
    const type = cmd[0];
    const nums = cmd.slice(1).trim().split(/[\s,]+/).map(Number);

    if (type === "M" && nums.length >= 2) {
      cx = nums[0] + offsetX;
      cy = nums[1] + offsetY;
      startX = cx;
      startY = cy;
    } else if (type === "L" && nums.length >= 2) {
      const nx = nums[0] + offsetX;
      const ny = nums[1] + offsetY;
      lines.push([cx, cy, nx, ny]);
      cx = nx;
      cy = ny;
    } else if (type === "Z" || type === "z") {
      lines.push([cx, cy, startX, startY]);
      cx = startX;
      cy = startY;
    }
    // Relative commands
    else if (type === "m" && nums.length >= 2) {
      cx += nums[0];
      cy += nums[1];
      startX = cx;
      startY = cy;
    } else if (type === "l" && nums.length >= 2) {
      const nx = cx + nums[0];
      const ny = cy + nums[1];
      lines.push([cx, cy, nx, ny]);
      cx = nx;
      cy = ny;
    }
  }
  return lines;
}

interface ViewData {
  visiblePaths: string[];
  hiddenPaths: string[];
  offsetX: number;
  offsetY: number;
}

export function exportDxf(views: ViewData[], annotations: Annotation[]): string {
  let entities = "";

  // Border
  entities += dxfLine(0, 0, SHEET_W, 0, "0");
  entities += dxfLine(SHEET_W, 0, SHEET_W, SHEET_H, "0");
  entities += dxfLine(SHEET_W, SHEET_H, 0, SHEET_H, "0");
  entities += dxfLine(0, SHEET_H, 0, 0, "0");

  // View geometry
  for (const view of views) {
    for (const pathStr of view.visiblePaths) {
      const segs = svgPathToLines(pathStr, view.offsetX, view.offsetY);
      for (const [x1, y1, x2, y2] of segs) {
        entities += dxfLine(x1, y1, x2, y2, "VISIBLE");
      }
    }
    for (const pathStr of view.hiddenPaths) {
      const segs = svgPathToLines(pathStr, view.offsetX, view.offsetY);
      for (const [x1, y1, x2, y2] of segs) {
        entities += dxfLine(x1, y1, x2, y2, "HIDDEN");
      }
    }
  }

  // Annotations → DXF entities
  for (const ann of annotations) {
    if (ann.kind === "linear") {
      const val = ann.textOverride || dist2d(ann.p1, ann.p2).toFixed(1);
      const mid: Vec2 = [(ann.p1[0] + ann.p2[0]) / 2, (ann.p1[1] + ann.p2[1]) / 2 - ann.offset];
      entities += dxfLine(ann.p1[0], ann.p1[1], ann.p2[0], ann.p2[1], "DIMENSIONS");
      entities += dxfText(mid[0], mid[1], 3, val, "DIMENSIONS");
    } else if (ann.kind === "radial") {
      entities += dxfCircle(ann.center[0], ann.center[1], ann.radius, "DIMENSIONS");
      entities += dxfText(ann.center[0], ann.center[1] - ann.radius - 3, 3, `R${ann.radius.toFixed(1)}`, "DIMENSIONS");
    } else if (ann.kind === "diameter") {
      entities += dxfCircle(ann.center[0], ann.center[1], ann.diameter / 2, "DIMENSIONS");
      entities += dxfText(ann.center[0], ann.center[1], 3, `%%C${ann.diameter.toFixed(1)}`, "DIMENSIONS");
    } else if (ann.kind === "text") {
      entities += dxfText(ann.position[0], ann.position[1], ann.fontSize, ann.text, "DIMENSIONS");
    } else if (ann.kind === "balloon") {
      entities += dxfCircle(ann.center[0], ann.center[1], ann.radius, "DIMENSIONS");
      entities += dxfText(ann.center[0], ann.center[1], 3, ann.itemNumber, "DIMENSIONS");
    } else if (ann.kind === "centerline") {
      if (ann.style === "line" && ann.p2) {
        entities += dxfLine(ann.p1[0], ann.p1[1], ann.p2[0], ann.p2[1], "DIMENSIONS");
      }
    }
  }

  return dxfHeader() + dxfTables() + `0
SECTION
2
ENTITIES
` + entities + `0
ENDSEC
0
EOF
`;
}

export function downloadDxf(views: ViewData[], annotations: Annotation[], filename: string): void {
  downloadBlob(new Blob([exportDxf(views, annotations)], { type: "application/dxf" }), filename);
}
